import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  demoLoginFlags,
  devLoginAccountsFor,
  type DevLoginAccount,
} from "@lms/backend/lib/auth/dev-login-accounts";
import { resolvePostLoginPath } from "@lms/backend/lib/auth/post-login-redirect";
import { OtpCodeForm } from "@/components/auth/otp-code-form";
import { DeviceChoiceForm } from "@/components/auth/device-choice-form";
import { Button, Card, Heading, PageShell, Text } from "@/components/ui";
import { Field, Input } from "@/components/ui/field";
import {
  cancelDeviceChoice,
  chooseDeviceToSignOut,
  fetchLoginRedirect,
  googleSignInUrl,
  isDeviceLimitStep,
  loginWithPassword,
  logoutSession,
  resendEmailOtp,
  resendLoginOtp,
  setOAuthTenant,
  verifyEmailOtpStep,
  verifyLoginOtp,
  type DeviceLimitStep,
  type SessionResult,
  type SignInResult,
} from "@/services/api/auth";
import { apiErrorText, readApiError } from "@/lib/api-error";
import { takePendingInvitePath } from "@/lib/pending-invite";
import { safeNextPath } from "@/lib/safe-next-path";
import { useAuthStore, type AuthUser } from "@/stores/authStore";
import { fetchTenantBootstrap } from "@/services/api/tenants";

type LoginFormProps = {
  idleReason?: boolean;
  tenantName?: string;
  /** MT-1 S24: the name of the school this browser is signed in at, when it is another one. */
  signedInSchoolName?: string;
  demoAccounts?: DevLoginAccount[];
  demoLoginsProduction?: boolean;
};

export function LoginForm({
  idleReason = false,
  tenantName,
  signedInSchoolName,
  demoAccounts = [],
  demoLoginsProduction = false,
}: LoginFormProps) {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const [loginParams] = useSearchParams();
  // C2: a session that could not be renewed lands here with ?reason=expired&next=<page>.
  const expiredReason = loginParams.get("reason") === "expired";
  // H3: this device was signed out so the account could sign in on another one.
  const otherDeviceReason = loginParams.get("reason") === "other_device";
  const suspendedReason = loginParams.get("reason") === "suspended";
  // W-29: the email was removed from the school's approved list.
  const notApprovedReason = loginParams.get("reason") === "not_approved";
  // S3 V-17: the sign-out was refused by the server (origin check); only this tab is signed out.
  const signoutFailedReason = loginParams.get("reason") === "signout_failed";
  const nextPath = safeNextPath(loginParams.get("next"), tenantSlug);
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);
  // MT-1 S24 (Q-S3P5-4): one school session per browser. Signed in at another school, this page
  // says so, and signing in here signs out there first.
  const sessionUser = useAuthStore((s) => s.user);
  const otherSchoolSlug =
    sessionUser?.tenantSlug && sessionUser.tenantSlug !== tenantSlug
      ? sessionUser.tenantSlug
      : null;
  const t = useTranslations("auth");
  const tOtp = useTranslations("auth.otp");
  const tRoot = useTranslations();
  // AUTH-13: where the visitor came from.
  const registeredNotice = loginParams.get("registered") === "1";
  const resetNotice = loginParams.get("reset") === "1";
  // SPA-10: a new school's admin arriving from platform signup.
  const signupNotice = loginParams.get("signup") === "1";
  // AUTH-7 / AUTH-14: the error code decides the message; the server's English never shows.
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, fallbackKey, { namespace: "auth.errors" });
  const [codeStep, setCodeStep] = useState<{
    purpose: "login" | "verify_email";
    challengeToken: string;
    maskedEmail: string;
    rememberDeviceOffered: boolean;
  } | null>(null);
  // H3 device limit: the student picks a device to sign out before this sign-in completes.
  const [deviceStep, setDeviceStep] = useState<DeviceLimitStep | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const googleEnabled = import.meta.env.VITE_AUTH_GOOGLE_ENABLED === "true";
  const e2eMode = import.meta.env.VITE_E2E_TEST_MODE === "true";
  const demoPassword = demoAccounts[0]?.password;

  async function redirectAfterLogin(sessionUser: AuthUser) {
    const invitePath = takePendingInvitePath(sessionUser.tenantSlug);
    if (invitePath) {
      navigate(invitePath);
      return;
    }
    if (nextPath && sessionUser.tenantSlug === tenantSlug) {
      navigate(nextPath);
      return;
    }
    try {
      const { redirectTo } = await fetchLoginRedirect();
      navigate(redirectTo);
    } catch {
      navigate(resolvePostLoginPath(sessionUser.tenantSlug, sessionUser.role));
    }
  }

  async function finishSession(session: SessionResult) {
    setSession(session.accessToken, session.user);
    await redirectAfterLogin(session.user);
  }

  async function finishSignIn(result: SignInResult) {
    if (isDeviceLimitStep(result)) {
      setCodeStep(null);
      setDeviceStep(result);
      return;
    }
    await finishSession(result);
  }

  async function chooseDevice(deviceId: string) {
    if (!deviceStep) return;
    let result: SignInResult;
    try {
      result = await chooseDeviceToSignOut({
        deviceChoiceToken: deviceStep.deviceChoiceToken,
        signOutDeviceId: deviceId,
      });
    } catch (err) {
      throw new Error(errorText(err, "auth.devices.failed"));
    }
    await finishSignIn(result);
  }

  function cancelDeviceStep() {
    if (deviceStep) void cancelDeviceChoice(deviceStep.deviceChoiceToken).catch(() => undefined);
    setDeviceStep(null);
    setPassword("");
  }

  async function signInWith(withEmail: string, withPassword: string) {
    setLoading(true);
    setError(null);

    if (otherSchoolSlug) {
      try {
        await logoutSession();
      } catch (err) {
        setError(errorText(err, "errors.generic"));
        setLoading(false);
        return;
      }
      clearSession();
    }

    try {
      const step = await loginWithPassword({
        email: withEmail,
        password: withPassword,
        tenantSlug,
      });
      if (step.kind === "session") {
        await finishSession(step);
      } else if (step.kind === "device_limit") {
        setDeviceStep(step);
      } else {
        setCodeStep({
          purpose: step.kind === "otp" ? "login" : "verify_email",
          challengeToken: step.challengeToken,
          maskedEmail: step.maskedEmail,
          rememberDeviceOffered: step.kind === "otp" && step.rememberDeviceOffered === true,
        });
      }
    } catch (err) {
      // LRN-15 (L3): right password, account pending deletion — offer to cancel it.
      if (readApiError(err)?.code === "ACCOUNT_PENDING_DELETION") {
        navigate(`/t/${tenantSlug}/account/restore?email=${encodeURIComponent(withEmail)}`);
        return;
      }
      setError(errorText(err, "auth.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(code: string, rememberDevice: boolean) {
    if (!codeStep) return;
    try {
      const result =
        codeStep.purpose === "login"
          ? await verifyLoginOtp({ challengeToken: codeStep.challengeToken, code, rememberDevice })
          : await verifyEmailOtpStep({ challengeToken: codeStep.challengeToken, code });
      await finishSignIn(result);
    } catch (err) {
      throw new Error(errorText(err, "auth.otp.failed"));
    }
  }

  async function resendCode() {
    if (!codeStep) return;
    try {
      await (codeStep.purpose === "login" ? resendLoginOtp : resendEmailOtp)(
        codeStep.challengeToken,
      );
    } catch (err) {
      throw new Error(errorText(err, "auth.otp.failed"));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await signInWith(email, password);
  }

  async function handleDevLogin(devEmail: string, devPassword: string) {
    setEmail(devEmail);
    setPassword(devPassword);
    await signInWith(devEmail, devPassword);
  }

  async function handleGoogleSignIn() {
    setLoading(true);
    setError(null);
    try {
      await setOAuthTenant(tenantSlug);
      window.location.href = googleSignInUrl();
    } catch (err) {
      setError(errorText(err, "errors.generic"));
      setLoading(false);
    }
  }

  return (
    <PageShell
      width="form"
      pad="none"
      className="flex min-h-[calc(100vh-5rem)] flex-col justify-center py-10"
    >
      <Card tone="brand" pad="none" className="rounded-2xl p-8 shadow-sm">
        <Heading as="p" level="eyebrow" className="mb-1" data-testid="login-tenant-eyebrow">
          {tenantName ?? tenantSlug}
        </Heading>
        <Heading as="h1" level="page" className="mb-2 text-3xl">
          {deviceStep
            ? t("devices.title")
            : codeStep
              ? tOtp(codeStep.purpose === "login" ? "loginTitle" : "verifyTitle")
              : t("signInTitle")}
        </Heading>
        {deviceStep ? (
          <DeviceChoiceForm
            key={deviceStep.deviceChoiceToken}
            step={deviceStep}
            onChoose={chooseDevice}
            onCancel={cancelDeviceStep}
          />
        ) : codeStep ? (
          <OtpCodeForm
            key={codeStep.challengeToken}
            purpose={codeStep.purpose}
            maskedEmail={codeStep.maskedEmail}
            offerRememberDevice={codeStep.rememberDeviceOffered}
            onSubmit={submitCode}
            onResend={resendCode}
            onCancel={() => {
              setCodeStep(null);
              setPassword("");
            }}
          />
        ) : (
          <>
            <Text tone="muted" className="mb-6">
              {t("tenantLabel", { tenant: tenantSlug })}
            </Text>
            {otherSchoolSlug && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-other-school-notice"
              >
                {t("otherSchoolSignedIn", { school: signedInSchoolName ?? otherSchoolSlug })}
              </Text>
            )}
            {registeredNotice && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-registered-notice"
              >
                {t("registeredNotice")}
              </Text>
            )}
            {signupNotice && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-signup-notice"
              >
                {t("signupNotice")}
              </Text>
            )}
            {resetNotice && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-reset-notice"
              >
                {t("passwordUpdatedNotice")}
              </Text>
            )}
            {expiredReason && !idleReason && (
              <Text className="mb-4 text-[var(--accent-ink)]" data-testid="login-expired-notice">
                {t("sessionExpiredNotice")}
              </Text>
            )}
            {otherDeviceReason && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-other-device-notice"
              >
                {t("otherDeviceNotice")}
              </Text>
            )}
            {suspendedReason && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-suspended-notice"
              >
                {t("suspendedNotice")}
              </Text>
            )}
            {notApprovedReason && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-not-approved-notice"
              >
                {t("notApprovedNotice")}
              </Text>
            )}
            {signoutFailedReason && (
              <Text
                role="status"
                className="mb-4 text-[var(--accent-ink)]"
                data-testid="login-signout-failed-notice"
              >
                {t("signoutFailedNotice")}
              </Text>
            )}
            {idleReason && (
              <Text className="mb-4 text-[var(--accent-ink)]" data-testid="login-idle-notice">
                {t("idleNotice")}
              </Text>
            )}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <Field label={t("email")}>
                <Input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field label={t("password")}>
                <Input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              {error && (
                <Text role="alert" className="text-[var(--danger)]" data-testid="login-error">
                  {error}
                </Text>
              )}
              <p className="text-right text-sm">
                <Link
                  to={`/t/${tenantSlug}/forgot-password`}
                  className="text-[var(--accent-ink)] underline underline-offset-4"
                  data-testid="login-forgot-password"
                >
                  {t("forgotPassword")}
                </Link>
              </p>
              <Button
                type="submit"
                variant="accent"
                data-testid="login-submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? t("signingIn") : t("signIn")}
              </Button>
            </form>
            {googleEnabled && !e2eMode && (
              <button
                type="button"
                data-testid="login-google"
                disabled={loading}
                onClick={() => void handleGoogleSignIn()}
                className="mt-3 w-full rounded-md border border-[var(--border-strong)] px-4 py-2 text-sm hover:bg-[var(--surface-3)] disabled:opacity-50"
              >
                {t("signInWithGoogle")}
              </button>
            )}
            {e2eMode && (
              <button
                type="button"
                data-testid="login-google-mock"
                disabled={loading}
                className="mt-3 w-full rounded-md border border-[var(--border-strong)] px-4 py-2 text-sm hover:bg-[var(--surface-3)] disabled:opacity-50"
                onClick={() => void signInWith("student@astromania.test", "Password1234")}
              >
                {t("googleE2eMock")}
              </button>
            )}
            {demoAccounts.length > 0 && (
              <section
                className="mt-6 rounded-lg border border-dashed border-[var(--border-strong)] p-4"
                data-testid="dev-logins"
              >
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">
                  {demoLoginsProduction ? t("demoLoginsTitle") : t("devLoginsTitle")}
                </p>
                <p className="mb-3 text-xs text-[var(--text-muted)]">
                  {demoLoginsProduction
                    ? t("demoLoginsHint", { password: demoPassword ?? "" })
                    : t("devLoginsHint")}
                </p>
                <ul className="space-y-2">
                  {demoAccounts.map((account) => (
                    <li key={account.email}>
                      <button
                        type="button"
                        data-testid={`dev-login-${account.role}`}
                        disabled={loading}
                        onClick={() => void handleDevLogin(account.email, account.password)}
                        className="flex w-full items-center justify-between gap-3 rounded-md border border-[var(--border-strong)] px-3 py-2 text-left text-sm hover:bg-[var(--surface-3)] disabled:opacity-50"
                      >
                        <span className="font-medium">{account.label}</span>
                        <span className="text-xs text-[var(--text-muted)]">{account.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <p className="mt-4 text-center text-sm">
              {t("noAccount")}{" "}
              <Link
                // PAY-9: a new account also comes back to where sign-in was asked for.
                to={`/t/${tenantSlug}/register${nextPath ? `?next=${encodeURIComponent(nextPath)}` : ""}`}
                className="text-[var(--accent-ink)] underline underline-offset-4"
              >
                {t("register")}
              </Link>
            </p>
          </>
        )}
        {/* H1b: a session now lasts 7 days idle, and closing the browser keeps it. */}
        <Text
          tone="muted"
          className="mt-6 text-center text-xs"
          data-testid="login-shared-computer-hint"
        >
          {t("sharedComputerHint")}
        </Text>
      </Card>
    </PageShell>
  );
}

export function TenantLoginPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  // SPA-11: the school's own name (TenantLayout already loaded it — same query, from cache).
  const bootstrapQuery = useQuery({
    queryKey: ["tenant-bootstrap", tenantSlug],
    queryFn: () => fetchTenantBootstrap(tenantSlug),
    enabled: Boolean(tenantSlug),
  });
  // MT-1 S24: the name of the school this browser is signed in at, if it is another one.
  const sessionSlug = useAuthStore((s) => s.user?.tenantSlug);
  const otherSlug = sessionSlug && sessionSlug !== tenantSlug ? sessionSlug : "";
  const otherSchoolQuery = useQuery({
    queryKey: ["tenant-bootstrap", otherSlug],
    queryFn: () => fetchTenantBootstrap(otherSlug),
    enabled: Boolean(otherSlug),
  });
  const isProduction = import.meta.env.PROD;
  // SEC-2: production builds show no demo logins unless VITE_DEMO_LOGINS=true, and no admin one
  // unless VITE_DEMO_ADMIN_LOGIN=true as well.
  const { demoLoginsEnabled, includeTenantAdmin } = demoLoginFlags({
    isProduction,
    demoLogins: import.meta.env.VITE_DEMO_LOGINS,
    demoAdminLogin: import.meta.env.VITE_DEMO_ADMIN_LOGIN,
  });
  const demoAccounts = devLoginAccountsFor(tenantSlug, {
    isProduction,
    demoLoginsEnabled,
    includeTenantAdmin,
  });

  return (
    <LoginForm
      idleReason={searchParams.get("reason") === "idle"}
      tenantName={bootstrapQuery.data?.tenant.name ?? tenantSlug}
      signedInSchoolName={otherSchoolQuery.data?.tenant.name}
      demoAccounts={demoAccounts}
      demoLoginsProduction={isProduction && demoLoginsEnabled}
    />
  );
}
